/**
 * Pure TEA update for SceneTitleBar title draft.
 *
 * Shell: `SceneTitleBar.vue` (workspace rename Cmd; synopsis stays on
 * `useSynopsisDraft`).
 */
import { assertNever } from "../lib/assertNever";
import type { UpdateFn } from "../lib/tea";

export type SceneTitleBarModel = {
  readonly titleDraft: string;
  readonly editing: boolean;
};

export const initialSceneTitleBarModel: SceneTitleBarModel = {
  titleDraft: "",
  editing: false,
};

export type SceneTitleBarMsg =
  | { readonly type: "BeginEdit" }
  | { readonly type: "Seed"; readonly title: string }
  | { readonly type: "DraftChanged"; readonly draft: string }
  | { readonly type: "Commit"; readonly canonicalTitle: string };

export type SceneTitleBarCmd = {
  readonly kind: "rename";
  readonly title: string;
};

export function updateSceneTitleBar(
  model: SceneTitleBarModel,
  msg: SceneTitleBarMsg,
): readonly [SceneTitleBarModel, readonly SceneTitleBarCmd[]] {
  switch (msg.type) {
    case "BeginEdit":
      return [{ ...model, editing: true }, []];
    case "Seed":
      return [{ titleDraft: msg.title, editing: false }, []];
    case "DraftChanged":
      return [{ ...model, titleDraft: msg.draft }, []];
    case "Commit": {
      const trimmed = model.titleDraft.trim();
      if (trimmed === msg.canonicalTitle) {
        return [{ titleDraft: msg.canonicalTitle, editing: false }, []];
      }
      return [
        { ...model, editing: false },
        [{ kind: "rename", title: trimmed }],
      ];
    }
    default:
      return assertNever(msg);
  }
}

export const updateSceneTitleBarFn: UpdateFn<
  SceneTitleBarModel,
  SceneTitleBarMsg,
  SceneTitleBarCmd
> = updateSceneTitleBar;
